import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { supplyChainService } from '../services/apiClient';

interface StockGraphModalProps {
  sku: string;
  warehouse: string;
  onClose: () => void;
}

const StockGraphModal: React.FC<StockGraphModalProps> = ({ sku, warehouse, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<any>(null);
  const [period, setPeriod] = useState<7 | 30 | 90 | 180>(90);

  useEffect(() => {
    fetchHistoricalData();
  }, [sku, warehouse, period]);

  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use supplyChainService which handles API URL correctly
      const data = await supplyChainService.getHistoricalStockData(sku, warehouse, period);
      setHistoryData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayData = () => {
    if (!historyData) return [];
    
    // Show last N days based on selected period
    return historyData.history.slice(-period);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Stock History</h2>
            <p className="text-sm text-gray-500 mt-1">
              {sku} • {warehouse}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600">Loading stock history...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800 font-medium">Error loading data</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {!loading && !error && historyData && (
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPeriod(7)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      period === 7
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    7 Days
                  </button>
                  <button
                    onClick={() => setPeriod(30)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      period === 30
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    30 Days
                  </button>
                  <button
                    onClick={() => setPeriod(90)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      period === 90
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    90 Days
                  </button>
                  <button
                    onClick={() => setPeriod(180)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      period === 180
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    180 Days
                  </button>
                </div>

                {/* Metrics */}
                <div className="flex space-x-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{historyData.current_stock.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Current Stock</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{historyData.in_stock_rate_90}%</div>
                    <div className="text-xs text-gray-500">In-Stock Rate (90d)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{historyData.in_stock_rate_180}%</div>
                    <div className="text-xs text-gray-500">In-Stock Rate (180d)</div>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-gray-50 rounded-lg p-4">
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    data={getDisplayData()}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorStock" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      stroke="#6b7280"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                      formatter={(value: any, name: string) => {
                        if (name === 'stock') return [value.toLocaleString(), 'Stock Level'];
                        if (name === 'sales') return [value.toLocaleString(), 'Daily Sales'];
                        return [value, name];
                      }}
                      labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="stock"
                      stroke="#2563eb"
                      fill="url(#colorStock)"
                      strokeWidth={2}
                      name="Stock Level"
                    />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      stroke="#10b981"
                      fill="url(#colorSales)"
                      strokeWidth={2}
                      name="Daily Sales"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Days with Stock (90d)</div>
                  <div className="text-2xl font-bold text-blue-900">{historyData.days_with_stock_90}</div>
                  <div className="text-xs text-gray-500">out of 90 days</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Days with Stock (180d)</div>
                  <div className="text-2xl font-bold text-green-900">{historyData.days_with_stock_180}</div>
                  <div className="text-xs text-gray-500">out of 180 days</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Estimated Min Stock</div>
                  <div className="text-2xl font-bold text-purple-900">
                    {Math.min(...getDisplayData().map((d: any) => d.stock)).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">in {period} days</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Estimated Max Stock</div>
                  <div className="text-2xl font-bold text-orange-900">
                    {Math.max(...getDisplayData().map((d: any) => d.stock)).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">in {period} days</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockGraphModal;











